import "./style.scss";
import { useMemo, useRef, useState, type FormEvent, type PointerEvent } from "react";
import { motion } from "framer-motion";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { Robot3D } from "./AuthLogin/Robot3D";
import packageJson from "../../../../package.json";

export const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [cursor, setCursor] = useState({ x: 0.5, y: 0.5 });
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [buttonHovered, setButtonHovered] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState<boolean>(() => {
    const stored = localStorage.getItem("remember_me");
    return stored ? JSON.parse(stored) : false;
  });
  const [error, setError] = useState("");
  const [dirty, setDirty] = useState(false);
  const loginCardRef = useRef<HTMLDivElement | null>(null);

  const { login } = useAuth();

  const handleRememberChange = (checked: boolean) => {
    setRemember(checked);
    localStorage.setItem("remember_me", JSON.stringify(checked));
  };

  const validateEmailOrUsername = (value: string) => {
    const emailRegex = /\S+@\S+\.\S+/;
    const usernameRegex = /^[a-zA-Z0-9_]{3,}$/;

    return emailRegex.test(value) || usernameRegex.test(value);
  };

  const validationMessage = useMemo(() => {
    if (!dirty) return "";
    if (!email || !password) return "Email/Username and password are required";
    if (!validateEmailOrUsername(email))
      return "Please enter a valid email or username";
    if (!password || password.length < 6)
      return "Password must be at least 6 characters";
    return "";
  }, [dirty, email, password]);

  const isFromvalid = validationMessage === "";

  const handleLogin = async () => {
    setDirty(true);
    setError("");
    setLoginSuccess(false);

    if (!email || !password) {
      setError("Email and password are required");
      return;
    }

    if (!validateEmailOrUsername(email)) {
      setError("Please enter a valid email or username");
      return;
    }

    if (!password || password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setIsSubmitting(true);

    try {
      await login({ username: email, password, remember });
      setLoginSuccess(true);
    } catch (loginError) {
      setError(
        loginError instanceof Error
          ? loginError.message
          : "Login failed. Please try again.",
      );
      setLoginSuccess(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await handleLogin();
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const rect = loginCardRef.current?.getBoundingClientRect();

    if (!rect) return;

    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;

    setCursor({
      x: Math.min(1, Math.max(0, x)),
      y: Math.min(1, Math.max(0, y)),
    });
  };

  return (
    <div className="login" ref={loginCardRef} onPointerMove={handlePointerMove}>
      <div className="login-container">
        <motion.section
          className="login-visual"
          initial={{ opacity: 0, x: -24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        >
          <div className="login-column login-column-left">
            <div className="login-copy">
              <span className="login-eyebrow">SORDO MADALENO | Architecture</span>
              <h1>Hours Tracking</h1>
              <p>
                A centralized workspace for labor tracking, live metrics, and
                operational visibility.
              </p>
            </div>

            <form className="login-form" onSubmit={handleSubmit}>
              <div className="div-inputs">
                <label className="login-field" htmlFor="login-identity">
                  <span className="login-field-label">Email or Username</span>
                  <input
                    id="login-identity"
                    className="login-input"
                    type="text"
                    placeholder="Email or Username"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (!dirty) setDirty(true);
                    }}
                    autoComplete="username"
                    autoCapitalize="none"
                    spellCheck={false}
                  />
                </label>

                <label className="login-field" htmlFor="login-password">
                  <span className="login-field-label">Password</span>
                  <div className="login-password-field">
                    <input
                      id="login-password"
                      className="login-input"
                      type={showPassword ? "text" : "password"}
                      placeholder="Password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (!dirty) setDirty(true);
                      }}
                      onFocus={() => setPasswordFocused(true)}
                      onBlur={() => setPasswordFocused(false)}
                      autoComplete="current-password"
                    />
                    <button
                      className="password-toggle"
                      type="button"
                      onClick={() => setShowPassword((current) => !current)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      aria-pressed={showPassword}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </label>
              </div>

              <button
                className="login-button"
                type="submit"
                onMouseEnter={() => setButtonHovered(true)}
                onMouseLeave={() => setButtonHovered(false)}
                disabled={isSubmitting}
                aria-disabled={isSubmitting}
                data-valid={isFromvalid}
              >
                {isSubmitting ? "Checking..." : "Login"}
              </button>

              <div className="login-form-meta">
                <div className="remember">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => handleRememberChange(e.target.checked)}
                  />
                  <span>Remember me</span>
                </div>

                <div className="errors" aria-live="polite">
                  {(validationMessage || error) && (
                    <div className="error">{validationMessage || error}</div>
                  )}
                  {loginSuccess && <div className="success">Access granted.</div>}
                </div>
              </div>
            </form>
          </div>

          <div className="login-column login-column-right">
            <div className="login-visual-robot">
              <Robot3D
                cursor={cursor}
                passwordFocused={passwordFocused}
                buttonHovered={buttonHovered}
                loginSuccess={loginSuccess}
              />
            </div>

            <div className="login-footer">
              v{packageJson.version} • IT & Software Development
            </div>
          </div>
        </motion.section>
      </div>
    </div>
  );
};
